import { useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { kebabCase } from 'lodash';

import { useLazyRequest } from '~hooks/index';
import { getCollectedItemsToExport } from '~services/route/report';
import { RouteReportCollectedItemsFilters } from '~services/route/report/types';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';
import { Nullable } from '~globals/types/commons';
import { ReportCollectedItemsMode } from '~globals/types/enums';

interface UseExportCollectedItemsParams {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseExportCollectedItemsReturn {
  loading: boolean;
  onExportCollectedItems: (
    params: RouteReportCollectedItemsFilters,
  ) => Promise<void>;
}

const getPefixByMode = (mode: Nullable<ReportCollectedItemsMode>): string => {
  let prefix = 'por';

  if (mode === ReportCollectedItemsMode.Drivers) prefix += ' choferes';
  if (mode === ReportCollectedItemsMode.Zones) prefix += ' zonas';
  if (mode === ReportCollectedItemsMode.Companies) prefix += ' compañias';

  return prefix;
};

const getDefaultFileName = (prefix: string): string => {
  const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

  return `seguimiento-colecta-${prefix}-${currentDate}.xlsx`;
};

export const useExportCollectedItems = (
  options?: UseExportCollectedItemsParams,
): UseExportCollectedItemsReturn => {
  const currentReportMode = useRef<Nullable<ReportCollectedItemsMode>>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingExportCollectedItems, , executeExportCollectedItems] =
    useLazyRequest({
      request: getCollectedItemsToExport,
      withPostSuccess: (response) => {
        const prefix = getPefixByMode(currentReportMode.current);

        if (response.headers && response.data) {
          const defaultFileName = getDefaultFileName(kebabCase(prefix));

          options?.onSuccess?.();

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha descargado el seguimiento ${prefix}`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            `No se pudo descargar el seguimiento ${prefix}, intente nuevamente`,
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        const prefix = getPefixByMode(currentReportMode.current);

        options?.onError?.();

        enqueueSnackbar(
          `No se pudo descargar el seguimiento ${prefix}, intente nuevamente`,
          { variant: 'error' },
        );
      },
    });

  const onExportCollectedItems = useCallback(
    async (params: RouteReportCollectedItemsFilters) => {
      currentReportMode.current = params.reportCollectedItemsMode;
      await executeExportCollectedItems(params);
      currentReportMode.current = null;
    },
    [executeExportCollectedItems],
  );

  return {
    loading: loadingExportCollectedItems,
    onExportCollectedItems,
  };
};
